import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addOccupationalLicenseAgencyWebsite, deleteOccupationalLicenseAgencyWebsite, fetchInitialState, updateOccupationalLicenseAgencyWebsite, updateOccupationalLicenseOption } from './occupationalLicenseSlice';
import FloatingLabelInput from '../../components/floatingLabelInput';
import Loader from '../../components/loader';
import { OptionCard, OptionCardRow, StepHeading, StepShell } from '../../components/optionCard';
import { showValidationError } from "../../admin/components/onboarding/apiHelper";


const InputSet = ({ index, data, handleChange, onDelete, error }) => (
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <div className={`${error ? "mb-4" : ""}`}>
                <FloatingLabelInput 
                    handleChange={(e) => handleChange(index, 'description', e.target.value)} 
                    value={data.description} 
                    name="description" 
                    type="text" 
                    label="Agency Name" 
                    required={true}
                />
            </div>
            <div>
                <FloatingLabelInput 
                    handleChange={(e) => handleChange(index, 'link', e.target.value)} 
                    value={data.link} 
                    name="link" 
                    type="text" 
                    label="Link" 
                    required={true}
                    error={error}
                />
            </div>
            {index !== 0 && (
                <button
                    onClick={() => onDelete(index)}
                    className="text-md hover:bg-red-300 text-red-700 py-2 px-4 rounded border border-dashed"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                    </svg>
                </button>
            )}
        </div>
  );
  


const OccupationalLicense = () => {
    const [linkErrors, setLinkErrors] = useState([]);
    const occupationalLicenseState = useSelector(state => state.occupational_license);
    const dispatch = useDispatch();

    const addInputSet = () => {
        const incompleteSets = occupationalLicenseState.data.agencyWebsite.map((set, index) => {
            const errors = [];
            
            // Check for empty fields
            if (set.description.trim() === '') errors.push("Agency Name is required");
            if (set.link.trim() === '') {
                errors.push("Link is required");
            } else if (!set.link.endsWith('.com') && !set.link.endsWith('.biz')) {
                // Check for valid link ending
                errors.push("Link must end with .com or .biz");
            }
            
            return errors.length ? `Set ${index + 1}: ${errors.join(', ')}` : null;
        }).filter(Boolean); // Filter out null values to get only incomplete sets
    
        if (incompleteSets.length === 0) {
            // If all fields are complete and valid, add a new input set
            dispatch(addOccupationalLicenseAgencyWebsite());
        } else {
            // Display a detailed error message if there are incomplete or invalid fields
            showValidationError('Please correct the following before adding a new set:', incompleteSets);
        }
    };

    const updateInputSet = (index, field, value) => {
        dispatch(updateOccupationalLicenseAgencyWebsite({ index, field, value }));
        if (field === 'link' && value.length > 0) {
            const isValidLink = value.endsWith('.com') || value.endsWith('.biz');
            setLinkErrors((prevErrors) => {
                const newErrors = [...prevErrors];
                newErrors[index] = isValidLink ? '' : 'Link must end with .com or .biz';
                return newErrors;
            });
            if (!isValidLink) return;
        }
        else {
            setLinkErrors((prevErrors) => {
                const newErrors = [...prevErrors];
                newErrors[index] = '';
                return newErrors;
            });
        }
    };

    const deleteInputSet = (index) => {
        dispatch(deleteOccupationalLicenseAgencyWebsite({index}))
    };

    useEffect(() => {
        dispatch(fetchInitialState())
    }, [dispatch])

    return (
    <>
        { occupationalLicenseState.loading && <Loader /> }
        <StepShell>
            <StepHeading className="break-words">
                Does your Town, City or County require an occupational or any other license type to conduct business?
            </StepHeading>

            {/* Yes/No */}
            <OptionCardRow>
                {/* Yes Option */}
                <OptionCard
                    selected={occupationalLicenseState.data._option === 'Yes'}
                    onClick={() => dispatch(updateOccupationalLicenseOption('Yes'))}
                >
                    Yes
                </OptionCard>

                {/* No Option */}
                <OptionCard
                    selected={occupationalLicenseState.data._option === 'No'}
                    onClick={() => dispatch(updateOccupationalLicenseOption('No'))}
                >
                    No
                </OptionCard>
            </OptionCardRow>


            {/* IF Yes allow to Enter Agency Website */}

            { occupationalLicenseState.data._option === 'Yes' && (
                <>
                    <h3 className="mt-12 text-center text-base font-medium text-[#0a1445] sm:text-lg">
                        Provide a link for the agency website to order license
                    </h3>
                    <div className="mt-4 mx-auto w-full max-w-2xl p-1 sm:p-5">
                        {occupationalLicenseState.data.agencyWebsite.map((set, index) => (
                            <InputSet 
                                key={index} 
                                index={index} 
                                data={set} 
                                handleChange={updateInputSet} 
                                onDelete={deleteInputSet}         
                                error={linkErrors[index]}
                            />
                        ))}
                        {occupationalLicenseState.data.agencyWebsite.length < 10 && (
                            <div className="flex justify-center"> {/* Flex container to center the button */}
                                <button
                                    onClick={addInputSet}
                                    className="rounded-xl border-2 border-dashed border-[#fb6635] px-5 py-2.5 text-sm font-semibold text-[#fb6635] transition-colors hover:bg-orange-50"
                                >
                                Add More
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </StepShell>
    </>
  )
}

export default OccupationalLicense;
